import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Shield, Headphones, ShoppingCart, Heart, RotateCcw } from 'lucide-react';
import { productsAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { updatePageSEO, seoTemplates, addWebSiteStructuredData } from '../utils/seo';
import Newsletter from '../components/layout/Newsletter';
import ProductRatingStars from '../components/ProductRatingStars';
import ProductCardSwipeGallery from '../components/product/ProductCardSwipeGallery';
import { BUSINESS } from '../config/businessInfo';

// Resim URL'sini dinamik olarak oluştur
const getImageUrl = (imagePath) => {
  // Resim yolu kontrolü
  if (!imagePath) {
    return '/placeholder-image.jpg';
  }
  
  // Eğer object ise url property'sini al
  let actualPath = imagePath;
  if (typeof imagePath === 'object' && imagePath.url) {
    actualPath = imagePath.url;
  } else if (typeof imagePath === 'string') {
    actualPath = imagePath;
  } else {
    return '/placeholder-image.jpg';
  }
  
  // Production'da HTTPS kullan
  if (process.env.NODE_ENV === 'production') {
    // HTTPS ile başlamıyorsa HTTPS ekle
    if (actualPath.startsWith('http://')) {
      return actualPath.replace('http://', 'https://');
    }
    // HTTPS ile başlıyorsa direkt döndür
    if (actualPath.startsWith('https://')) {
      return actualPath;
    }
    // Relative path ise HTTPS domain ile birleştir
    if (actualPath.startsWith('/')) {
      return `https://leasertech.com${actualPath}`;
    }
    return `https://leasertech.com/${actualPath}`;
  }
  
  // Development'ta localhost kullan
  const API_BASE_URL = 'http://localhost:5000';
  
  // Eğer dış kaynak URL ise proxy kullan
  if (actualPath.startsWith('http://') || actualPath.startsWith('https://')) {
    return `${API_BASE_URL}/proxy-image?url=${encodeURIComponent(actualPath)}`;
  }
  
  // Yerel resim için normal yol
  const cleanedPath = actualPath.startsWith('/') ? actualPath : `/${actualPath}`;
  return `${API_BASE_URL}${cleanedPath}`;
};

// Skeleton Loading Components
const ProductCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
    <div className="bg-gray-200 h-48 w-full"></div>
    <div className="p-4">
      <div className="bg-gray-200 h-4 rounded mb-2"></div>
      <div className="bg-gray-200 h-4 rounded w-3/4 mb-2"></div>
      <div className="bg-gray-200 h-6 rounded w-1/2 mb-3"></div>
      <div className="flex justify-between items-center">
        <div className="bg-gray-200 h-8 w-20 rounded"></div>
        <div className="bg-gray-200 h-8 w-8 rounded"></div>
      </div>
    </div>
  </div>
);

const FeaturedProductsSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {[...Array(4)].map((_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </div>
);

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  const [currentImageIndex, setCurrentImageIndex] = useState({});

  const { addToCart, loading: cartLoading } = useCart();
  const { isAuthenticated } = useAuth();

  const loadFeaturedProducts = useCallback(async () => {
    try {
      const response = await productsAPI.getFeaturedProducts();
      setFeaturedProducts(response.data.products);
    } catch (error) {
      // Hata durumunda sessizce devam et
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // SEO Setup
    updatePageSEO(
      seoTemplates.home.title,
      seoTemplates.home.description,
      seoTemplates.home.keywords,
      'https://leasertech.com/og-image.jpg',
      'https://leasertech.com/'
    );
    addWebSiteStructuredData();
    
    loadFeaturedProducts();
  }, [loadFeaturedProducts]);

  useEffect(() => {
    if (isAuthenticated) {
      loadFavorites();
    }
  }, [isAuthenticated]);

  const handleAddToCart = async (productId, e) => {
    e.preventDefault(); // Link'e tıklanmasını engelle
    e.stopPropagation(); // Event bubbling'i engelle
    
    // Artık misafir kullanıcılar da sepete ekleyebilir
    await addToCart(productId, 1);
  };

  const handleToggleFavorite = async (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      window.location.href = '/login';
      return;
    }

    try {
      if (favorites.has(productId)) {
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          newFavorites.delete(productId);
          localStorage.setItem('favorites', JSON.stringify([...newFavorites]));
          return newFavorites;
        });
      } else {
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          newFavorites.add(productId);
          localStorage.setItem('favorites', JSON.stringify([...newFavorites]));
          return newFavorites;
        });
      }
    } catch (error) {
      // Hata durumunda sessizce devam et
    }
  };

  const loadFavorites = async () => {
    try {
      const savedFavorites = localStorage.getItem('favorites');
      if (savedFavorites) {
        const favoriteIds = new Set(JSON.parse(savedFavorites));
        setFavorites(favoriteIds);
      }
    } catch (error) {
      // Hata durumunda sessizce devam et
    }
  };

  const handleImageHover = (productId, direction) => {
    const product = featuredProducts.find(p => p.id === productId);
    
    if (!product || !product.images || product.images.length <= 1) {
      return;
    }

    const currentIndex = currentImageIndex[productId] || 0;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % product.images.length;
    } else {
      newIndex = currentIndex === 0 ? product.images.length - 1 : currentIndex - 1;
    }

    // Sadece index değiştiyse güncelle
    if (newIndex !== currentIndex) {
      setCurrentImageIndex(prev => ({
        ...prev,
        [productId]: newIndex
      }));
    }
  };

  const categories = [
    { name: 'Bilgisayar Aksesuarları', image: '/api/placeholder/300/200', link: '/products?category=Bilgisayar Aksesuarları' },
    { name: 'Telefon Aksesuarları', image: '/api/placeholder/300/200', link: '/products?category=Telefon Aksesuarları' },
    { name: 'Şarj Aleti', image: '/api/placeholder/300/200', link: '/products?category=Şarj Aleti' },
    { name: 'Powerbank', image: '/api/placeholder/300/200', link: '/products?category=Powerbank' },
    { name: 'Telefon Tutacakları', image: '/api/placeholder/300/200', link: '/products?category=Telefon Tutacakları' },
    { name: 'Kulaklık', image: '/api/placeholder/300/200', link: '/products?category=Kulaklık' },
    { name: 'Hoparlör', image: '/api/placeholder/300/200', link: '/products?category=Hoparlör' },
    { name: 'Otomobil Ürünleri', image: '/api/placeholder/300/200', link: '/products?category=Otomobil Ürünleri' },
    { name: 'Projeksiyon Cihazları', image: '/api/placeholder/300/200', link: '/products?category=Projeksiyon Cihazları' },
  ];

  const features = [
    {
      icon: <Truck className="h-8 w-8 text-orange-400" />,
      title: 'Hızlı Teslimat',
      description: 'Türkiye geneli 1-2 iş gününde teslimat'
    },
    {
      icon: <Shield className="h-8 w-8 text-orange-400" />,
      title: 'Güvenli Alışveriş',
      description: 'SSL sertifikası ile güvenli ödeme'
    },
    {
      icon: <Headphones className="h-8 w-8 text-orange-400" />,
      title: '7/24 Destek',
      description: 'Müşteri hizmetleri her zaman yanınızda'
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-white/15 rounded-full blur-lg animate-pulse delay-500"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              
              <h1 className="text-xl lg:text-3xl font-bold mb-3 leading-tight">
                <span className="block">LeaserTech ile Alışveriş</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-orange-100">
                
                </span>
                <span className="block text-4xl lg:text-4xl mt-1 text-orange-50">
                  Keyifli ve Güvenli
                </span>
              </h1>
              
              <p className="text-base lg:text-lg mb-5 text-orange-50 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Bluetooth kulaklık, powerbank, ses bombası, bluetooth hoparlör ve taşınabilir projeksiyon cihazları
                <span className="font-semibold text-yellow-200"> en uygun fiyatlarla </span>
                kapınızda!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-4">
                <Link
                  to="/products"
                  className="group bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300 inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <span className="mr-2">Alışverişe Başla</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/products"
                  className="border-2 border-white/30 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300 backdrop-blur-sm hover:border-white transform hover:-translate-y-1"
                >
                  Tüm Ürünler
                </Link>
              </div>
              
            </div>
            
            {/* Right Content - Visual Elements */}
            <div className="relative hidden lg:block">
              <div className="relative z-10">
                {/* Main Card */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-4 text-center">
                    <div className="text-3xl mb-3">📱</div>
                    <h3 className="text-lg font-bold text-white mb-1">Teknoloji Ürünleri</h3>
                    <p className="text-orange-100 text-xs">En son teknoloji ürünleri</p>
                  </div>
                </div>
                
                {/* Floating Cards */}
                <div className="absolute -top-4 -right-4 bg-white/15 backdrop-blur-sm rounded-xl p-3 border border-white/30 animate-bounce">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium">Canlı Destek</span>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -left-4 bg-white/15 backdrop-blur-sm rounded-xl p-3 border border-white/30 animate-pulse">
                  <div className="flex items-center space-x-2">
                    <div className="text-sm">🚚</div>
                    <div>
                      <div className="text-xs font-bold">Hızlı Teslimat</div>
                      <div className="text-xs text-orange-200">1-2 gün</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 rounded-full blur-3xl scale-150"></div>
            </div>
          </div>
        </div>
        
        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-12 text-white" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" fill="currentColor" opacity="0.5"></path>
          </svg>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-8 sm:py-12 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
              Popüler Kategoriler
            </h2>
            <p className="text-sm sm:text-base text-gray-600 px-4">
              İhtiyacınıza uygun kategoriyi seçin ve alışverişe başlayın
            </p>
          </div>
          
          {/* Scrollable Categories */}
          <div className="relative px-4">
            <div 
              id="categoriesScroll"
              className="flex gap-3 sm:gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-2 sm:px-6 justify-center"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {categories.map((category, index) => (
                <Link
                  key={index}
                  to={category.link}
                  className="flex-shrink-0 group bg-gradient-to-br from-white to-orange-50 rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg hover:shadow-xl sm:hover:shadow-2xl transition-all duration-500 p-3 sm:p-5 text-center w-28 sm:w-36 relative overflow-hidden border border-orange-100 hover:border-orange-200"
                >
                  {/* Floating Elements - Smaller on Mobile */}
                  <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 bg-orange-200 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                  <div className="absolute -bottom-1 -left-1 w-4 h-4 sm:w-6 sm:h-6 bg-orange-300 rounded-full opacity-15 group-hover:opacity-30 transition-opacity duration-500"></div>
                  
                  {/* Content */}
                  <div className="relative z-10 flex flex-col items-center justify-center h-full">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-300 leading-tight text-center">
                      {category.name}
                    </h3>
                    
                    {/* Decorative Line */}
                    <div className="mt-2 sm:mt-3 w-6 sm:w-8 h-0.5 sm:h-1 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full group-hover:scale-110 transition-transform duration-300"></div>
                  </div>
                  
                  {/* Hover Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-400/10 to-orange-600/10 rounded-xl sm:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Öne Çıkan Ürünler
            </h2>
            <p className="text-gray-600">
              En popüler ve en çok tercih edilen ürünlerimiz
            </p>
          </div>

          {loading ? (
            <FeaturedProductsSkeleton />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {featuredProducts.map((product) => (
                <div
                  key={product.id}
                  className="group bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col h-full"
                  onMouseLeave={() => setCurrentImageIndex(prev => ({
                    ...prev,
                    [product.id]: 0
                  }))}
                >
                  <Link
                    to={`/product/${product.id}`}
                    className="block flex-1 flex flex-col"
                  >
                  <div className="relative bg-gray-100 group">
                    {/* Favori İkonu */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleFavorite(product.id, e);
                      }}
                      className="absolute top-2 right-2 z-30 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group/favorite cursor-pointer"
                      title="Favorilere Ekle"
                    >
                      <Heart className={`h-4 w-4 transition-colors ${
                        favorites.has(product.id) 
                          ? 'text-red-500 fill-red-500' 
                          : 'text-gray-400 group-hover/favorite:text-red-500'
                      }`} />
                    </button>

                    {/* Görsel Kaydırma Alanları - Trendyol Tarzı */}
                    {product.images && product.images.length > 1 && (
                      <>
                        <div
                          onMouseEnter={() => handleImageHover(product.id, 'prev')}
                          className="absolute left-0 top-0 w-1/2 h-full z-10 cursor-pointer hidden md:block"
                          title="Önceki Görsel"
                        />
                        <div
                          onMouseEnter={() => handleImageHover(product.id, 'next')}
                          className="absolute right-0 top-0 w-1/2 h-full z-10 cursor-pointer hidden md:block"
                          title="Sonraki Görsel"
                        />
                      </>
                    )}
                    
                    <ProductCardSwipeGallery
                      images={product.images}
                      currentIndex={currentImageIndex[product.id] || 0}
                      onIndexChange={(next) =>
                        setCurrentImageIndex((prev) => ({
                          ...prev,
                          [product.id]: next,
                        }))
                      }
                      getImageUrl={getImageUrl}
                      productName={product.name}
                      brand={product.brand}
                      categories={product.categories}
                    />
                  </div>
                  <div className="p-3 sm:p-4 flex-1 flex flex-col">
                    <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 line-clamp-2 text-sm sm:text-base">
                      {product.name}
                    </h3>
                    <ProductRatingStars
                      ratingAverage={product.rating_average}
                      ratingCount={product.rating_count}
                      className="mb-1 sm:mb-2"
                    />
                    <p className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">
                      ₺{product.price.toLocaleString()}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mb-1 sm:mb-2">
                      {product.brand}
                    </p>
                    <div className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 flex-1">
                      <p className="whitespace-pre-line line-clamp-2 sm:line-clamp-3">
                        {product.description}
                      </p>
                    </div>
                  </div>
                  </Link>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 p-3 sm:p-4 pt-0 mt-auto">
                    <button
                      onClick={(e) => handleAddToCart(product.id, e)}
                      disabled={cartLoading || product.stock === 0}
                      className={`w-full flex items-center justify-center gap-1 py-2 px-2 sm:px-3 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                        product.stock > 0 && !cartLoading
                          ? 'bg-orange-600 text-white hover:bg-orange-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">{cartLoading ? '...' : 'Sepete Ekle'}</span>
                      <span className="sm:hidden">{cartLoading ? '...' : 'Ekle'}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-8">
            <Link
              to="/products"
              className="inline-flex items-center bg-orange-400 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-500 transition-colors"
            >
              Tüm Ürünleri Gör
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f97316' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-orange-100 hover:border-orange-200"
              >
                {/* Floating Background Element */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-orange-100 rounded-full opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
                
                {/* Icon Container */}
                <div className="relative mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200/30 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-500">
                    <div className="text-white transform group-hover:rotate-6 transition-transform duration-500">
                      {feature.icon}
                    </div>
                  </div>
                  
                  {/* Pulse Animation */}
                  <div className="absolute inset-0 w-16 h-16 bg-gray-200 rounded-2xl opacity-20 animate-ping"></div>
                </div>

                {/* Content */}
                <div className="relative">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>

                {/* Hover Effect Line */}
                <div className="absolute bottom-0 left-8 right-8 h-1 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </div>
            ))}
          </div>
          {/* Bottom Stats */}
        </div>
      </section>


      {/* Güvenli Alışveriş */}
      <section className="py-8 sm:py-12 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Güvenli Alışveriş</h2>
            <p className="text-gray-600">
              LeaserTech yalnızca online satış yapar. Tüm siparişler kargo ile gönderilir.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Shield, title: 'Güvenli Ödeme', desc: '256-bit SSL ve iyzico ile kredi kartı ödeme' },
              { icon: Truck, title: 'Hızlı Kargo', desc: 'Türkiye geneline 3-5 iş günü teslimat' },
              { icon: RotateCcw, title: '14 Gün İade', desc: 'Koşullara uygun ürünlerde iade hakkı', link: '/returns' },
              { icon: Headphones, title: 'Müşteri Desteği', desc: `${BUSINESS.emailInfo} · ${BUSINESS.phone}`, link: '/contact' }
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-lg shadow-sm p-5 border border-orange-100">
                <item.icon className="h-8 w-8 text-orange-500 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
                {item.link && (
                  <Link to={item.link} className="inline-block mt-2 text-sm text-orange-600 hover:underline">
                    Detaylı bilgi →
                  </Link>
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            <Link to="/returns" className="text-orange-600 hover:underline">İade politikası</Link>
            {' · '}
            <Link to="/shipping" className="text-orange-600 hover:underline">Kargo bilgileri</Link>
            {' · '}
            <Link to="/distance-selling" className="text-orange-600 hover:underline">Mesafeli satış sözleşmesi</Link>
          </p>
        </div>
      </section>

      {/* SEO İçerik Bölümü */}
      <section className="bg-gray-50 py-12 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Elektronik Aksesuarlar | LeaserTech
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed max-w-4xl">
            LeaserTech olarak bluetooth kulaklık, kablosuz kulaklık, kablolu kulaklık, powerbank,
            ses bombası, bluetooth hoparlör, bt hoparlör ve taşınabilir projeksiyon cihazları
            kategorilerinde geniş ürün yelpazesi sunuyoruz. Oyun projeksiyonu ve game projeksiyon
            modelleri dahil tüm elektronik aksesuarları uygun fiyat, hızlı kargo ve güvenli ödeme
            ile satın alabilirsiniz.
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Bluetooth Kulaklık', search: 'bluetooth kulaklık' },
              { label: 'Kablosuz Kulaklık', search: 'kablosuz kulaklık' },
              { label: 'Powerbank', search: 'powerbank' },
              { label: 'Ses Bombası', search: 'ses bombası' },
              { label: 'Bluetooth Hoparlör', search: 'bluetooth hoparlör' },
              { label: 'Projeksiyon Cihazı', search: 'projeksiyon cihazı' },
              { label: 'Oyun Projeksiyonu', search: 'oyun projeksiyonu' },
              { label: 'Taşınabilir Projeksiyon', search: 'taşınabilir projeksiyon' }
            ].map((item) => (
              <Link
                key={item.search}
                to={`/products?search=${encodeURIComponent(item.search)}`}
                className="px-3 py-1.5 bg-white border border-orange-200 text-orange-700 rounded-full text-sm hover:bg-orange-50 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <Newsletter />
    </div>
  );
};

export default Home;

